import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData, siteConfig, timelineData, faqData } from '@/mocks/site';

export default function About() {
  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    foundingDate: String(siteConfig.foundingYear),
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutData.contact.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '杭州',
      addressRegion: '浙江',
      addressCountry: 'CN',
    },
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrg) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaq) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-sm mb-12" aria-label="面包屑导航">
              <a href="/" className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">首页</a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">关于我们</span>
            </nav>

            {/* Brand Intro */}
            <section className="mb-20">
              <div className="flex flex-col lg:flex-row items-center gap-10">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-5">
                    {aboutData.brand.title}
                  </h1>
                  <p className="text-base text-foreground-700 leading-relaxed mb-4">
                    {aboutData.brand.intro}
                  </p>
                  <p className="text-base text-foreground-700 leading-relaxed mb-4">
                    {aboutData.brand.mission}
                  </p>
                  <p className="text-base text-foreground-700 leading-relaxed">
                    {aboutData.brand.vision}
                  </p>
                </div>
                <div className="flex-1 w-full">
                  <img
                    src={aboutData.brand.imageUrl}
                    alt="撸撸社品牌"
                    className="w-full h-64 md:h-80 object-cover rounded-lg"
                    title="撸撸社 - 兴趣爱好交流社区"
                  />
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                我们的价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutData.brand.values.map((v, i) => (
                  <div key={i} className="p-6 rounded-lg bg-background-100 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50">
                      <span className="text-primary-500 font-heading text-lg">{i + 1}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 whitespace-nowrap">{v.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Timeline */}
            <section className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200/70 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {timelineData.map((item, i) => (
                    <div key={i} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="hidden md:flex flex-1"></div>
                      <div className="relative z-10">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i % 3 === 0 ? 'bg-primary-500' : i % 3 === 1 ? 'bg-accent-500' : 'bg-secondary-500'}`}>
                          <i className={`${item.icon} text-sm text-white`}></i>
                        </div>
                      </div>
                      <div className="flex-1 bg-background-100 rounded-lg p-5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-primary-500 bg-primary-50 px-2 py-0.5 rounded whitespace-nowrap">{item.year}</span>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-950 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Milestones */}
            <section className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                发展里程碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutData.milestones.map((m, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-100 border-l-4 border-primary-500">
                    <span className="text-xs font-bold text-primary-500 bg-primary-50 px-2 py-0.5 rounded whitespace-nowrap">{m.year}</span>
                    <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutData.reputation.map((r, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-100">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={r.avatar}
                        alt={r.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{r.name}</div>
                        <div className="text-xs text-foreground-500 whitespace-nowrap">{r.role}</div>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {Array.from({ length: r.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-xs text-secondary-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{r.content}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-20">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqData.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between p-5 text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: aboutData.contact.email, color: 'primary' },
                  { icon: 'ri-wechat-line', label: '微信公众号', value: aboutData.contact.wechat, color: 'accent' },
                  { icon: 'ri-weibo-line', label: '官方微博', value: aboutData.contact.weibo, color: 'secondary' },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: aboutData.contact.address, color: 'primary' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-6 rounded-lg bg-background-100">
                    <div className={`w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full ${item.color === 'primary' ? 'bg-primary-50' : item.color === 'accent' ? 'bg-accent-50' : 'bg-secondary-50'}`}>
                      <i className={`${item.icon} text-xl ${item.color === 'primary' ? 'text-primary-500' : item.color === 'accent' ? 'text-accent-500' : 'text-secondary-500'}`}></i>
                    </div>
                    <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                    <div className="text-sm font-medium text-foreground-950 break-all">{item.value}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}